// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DivWrapper } from "@werner/XDL/constants";
import { xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    padding = "padding",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.padding]: string;
    };
}

type Props = OwnProps;

interface ContainerWidgetStyles {
    container?: InjectableCss;
}

const containerWidgetStyles: ContainerWidgetStyles = {
    container: {
        css: css`
            width: 100%;

            ${({ backgroundColor, borderColor, padding }: any) => {
                return css`
                    ${backgroundColor && `background: ${backgroundColor};`}
                    ${borderColor && `border: 1px solid  ${borderColor};`}
                    ${padding && `padding: ${padding};`}
                `;
            }}

            &:has(${DivWrapper}:empty) {
                display: none;
            }
        `,
    },
};

const ContainerWidget: React.FC<Props> = ({ fields: { backgroundColor, borderColor, padding }, id }) => {
    const styles = containerWidgetStyles;
    return (
        <StyledWrapper {...styles.container} {...({ backgroundColor, borderColor, padding } as any)}>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContainerWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                isRequired: false,
            },
            {
                fieldType: "General",
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Border Color",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.padding,
                displayName: "Padding Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                placeholder: "Enter any padding style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
            },
        ],
    },
};

export default widgetModule;
